/**
 * Knowledge Hub Page
 * Comprehensive knowledge management - datasources, embedding models, vector stores, search
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Database,
  Search,
  Settings,
  Upload,
  Trash2,
  RefreshCw,
  Brain,
  Layers,
  FileText,
  Zap,
  CheckCircle,
  AlertCircle,
  Globe,
  Code,
  DollarSign,
  Building2,
  ChevronRight,
  Plus,
  BarChart3,
  BookOpen,
} from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { KnowledgeBase } from '../../components/knowledge';
import { KnowledgeSidebar } from './components/KnowledgeSidebar';
import { QuestionsAudit } from './components/QuestionsAudit';
import { IntegrationsPage } from '../IntegrationsPage';
import { DatasourcesPage } from '../DatasourcesPage';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui';
import api, { 
  Assistant, 
  Datasource, 
  EmbeddingConfig, 
  EmbeddingModelInfo,
} from '../../services/api';
import styles from './KnowledgeBasePage.module.css';

type TabId = 'overview' | 'datasources' | 'models' | 'search' | 'questions';

interface ModelCardProps {
  model: EmbeddingModelInfo;
  isActive: boolean;
  onSelect: () => void;
}

const ModelCard: React.FC<ModelCardProps> = ({ model, isActive, onSelect }) => {
  const providerIcons: Record<string, React.ReactNode> = {
    openai: <Zap size={16} />,
    voyage: <Code size={16} />,
    cohere: <Globe size={16} />,
  };

  return (
    <div 
      className={`${styles.modelCard} ${isActive ? styles.modelCardActive : ''}`}
      onClick={onSelect}
    >
      <div className={styles.modelCardHeader}>
        <div className={styles.modelCardIcon}>
          {providerIcons[model.provider] || <Brain size={16} />}
        </div>
        <div className={styles.modelCardTitle}>
          <h4>{model.model}</h4>
          <span className={styles.modelCardProvider}>{model.provider}</span>
        </div>
        {isActive && (
          <span className={styles.activeBadge}>
            <CheckCircle size={14} /> Active
          </span>
        )}
      </div>
      <p className={styles.modelCardDesc}>{model.description}</p>
      <div className={styles.modelCardStats}>
        <div className={styles.modelStat}>
          <span className={styles.modelStatLabel}>Dimensions</span>
          <span className={styles.modelStatValue}>{model.dimensions}</span>
        </div>
        <div className={styles.modelStat}>
          <span className={styles.modelStatLabel}>MTEB Score</span>
          <span className={styles.modelStatValue}>{model.mtebScore}</span>
        </div>
        <div className={styles.modelStat}>
          <span className={styles.modelStatLabel}>Cost/M Tokens</span>
          <span className={styles.modelStatValue}>${model.pricePerMillionTokens}</span>
        </div>
        <div className={styles.modelStat}>
          <span className={styles.modelStatLabel}>Max Tokens</span>
          <span className={styles.modelStatValue}>{model.maxTokens.toLocaleString()}</span>
        </div>
      </div>
      <div className={styles.modelCardTags}>
        {model.bestFor.map((tag) => (
          <span key={tag} className={styles.modelCardTag}>{tag}</span>
        ))}
      </div>
      <div className={styles.modelCardFooter}>
        <div className={styles.prosConsList}>
          {model.pros.slice(0, 2).map((pro, i) => (
            <span key={i} className={styles.proItem}>✓ {pro}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const KnowledgeBasePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = (queryParams.get('tab') as TabId) || 'overview';
  const setActiveTab = (tab: TabId) => {
    if (tab === 'overview') navigate('/knowledge');
    else navigate(`/knowledge?tab=${tab}`);
  };
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Embedding config state
  const [embeddingConfig, setEmbeddingConfig] = useState<EmbeddingConfig | null>(null);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isSettingModel, setIsSettingModel] = useState(false);
  
  // Global datasources state
  const [globalDatasources, setGlobalDatasources] = useState<Datasource[]>([]);
  const [isLoadingDatasources, setIsLoadingDatasources] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Test embedding state
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [assistantsRes, embeddingRes, dsRes] = await Promise.all([
          api.listAssistants(),
          api.getEmbeddingConfig(),
          api.listAllDatasources(),
        ]);
        
        if (assistantsRes.data) {
          setAssistants(Array.isArray(assistantsRes.data) ? assistantsRes.data : []);
          if (Array.isArray(assistantsRes.data) && assistantsRes.data.length > 0) {
            setSelectedAssistant(assistantsRes.data[0]);
          }
        }
        
        if (embeddingRes.success && embeddingRes.data) {
          setEmbeddingConfig(embeddingRes.data);
        }
        
        if (dsRes.success && dsRes.data) {
          setGlobalDatasources(Array.isArray(dsRes.data) ? dsRes.data : []);
        }
      } catch (err) {
        console.error('Failed to load knowledge hub data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Handle model selection
  const handleSelectModel = async (model: EmbeddingModelInfo) => {
    setIsSettingModel(true);
    try {
      await api.setEmbeddingProvider(model.provider, model.model, model.dimensions);
      // Refresh config
      const res = await api.getEmbeddingConfig();
      if (res.success && res.data) {
        setEmbeddingConfig(res.data);
      }
    } catch (err) {
      console.error('Failed to set embedding model:', err);
    } finally {
      setIsSettingModel(false);
    }
  };

  // Handle use case preset
  const handleSetUseCase = async (useCase: string) => {
    setIsSettingModel(true);
    try {
      await api.setEmbeddingUseCase(useCase);
      const res = await api.getEmbeddingConfig();
      if (res.success && res.data) {
        setEmbeddingConfig(res.data);
      }
    } catch (err) {
      console.error('Failed to set use case:', err);
    } finally {
      setIsSettingModel(false);
    }
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim() || !selectedAssistant) return;
    setIsSearching(true);
    try {
      const res = await api.searchKnowledgeBase(selectedAssistant.id, searchQuery);
      if (res.success && res.data) {
        setSearchResults(res.data.chunks || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle test embedding
  const handleTestEmbedding = async () => {
    if (!testText.trim()) return;
    setIsTesting(true);
    try {
      const res = await api.testEmbedding(testText);
      if (res.success && res.data) {
        setTestResult(res.data);
      }
    } catch (err) {
      console.error('Embedding test failed:', err);
    } finally {
      setIsTesting(false);
    }
  };

  // Handle assistant change
  const handleAssistantChange = (id: string) => {
    const assistant = assistants.find(a => a.id === id);
    if (assistant) setSelectedAssistant(assistant);
  };

  // Preset cards data
  const presetCards = [
    { key: 'budget', icon: <DollarSign size={20} />, title: 'Budget', desc: 'Cost-effective, great for startups', color: '#10b981' },
    { key: 'enterprise', icon: <Building2 size={20} />, title: 'Enterprise', desc: 'High accuracy for production', color: '#6366f1' },
    { key: 'code', icon: <Code size={20} />, title: 'Code', desc: 'Optimized for code & technical docs', color: '#f59e0b' },
    { key: 'multilingual', icon: <Globe size={20} />, title: 'Multilingual', desc: '100+ languages supported', color: '#ec4899' },
  ];

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 size={16} /> },
    { id: 'datasources', label: 'Datasources', icon: <Database size={16} /> },
    { id: 'questions', label: 'Questions & Audit', icon: <BookOpen size={16} /> },
    { id: 'models', label: 'Embedding Models', icon: <Brain size={16} /> },
    { id: 'search', label: 'Search & Test', icon: <Search size={16} /> },
  ];

  return (
    <AppLayout sidebar={<KnowledgeSidebar />}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.content} style={{ padding: location.pathname !== '/knowledge' ? '0' : undefined }}>
          {isLoading && location.pathname === '/knowledge' ? (
            <div className={styles.loadingState}>Loading Knowledge Hub...</div>
          ) : (
            <>
              {location.pathname === '/knowledge/integrations' && <IntegrationsPage embedded={true} />}
              {location.pathname === '/knowledge/datasources' && <DatasourcesPage embedded={true} />}
              
              {/* ============ OVERVIEW TAB ============ */}
              {location.pathname === '/knowledge' && activeTab === 'overview' && (
                <div className={styles.overviewTab}>
                  {/* Stats Row */}
                  <div className={styles.statsRow}>
                    <div className={styles.statCard}>
                      <div className={styles.statIcon} style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                        <Database size={20} />
                      </div>
                      <div>
                        <div className={styles.statValue}>{globalDatasources.length}</div>
                        <div className={styles.statLabel}>Datasources</div>
                      </div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statIcon} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <Layers size={20} />
                      </div>
                      <div>
                        <div className={styles.statValue}>{embeddingConfig?.models.length || 0}</div>
                        <div className={styles.statLabel}>Available Models</div>
                      </div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                        <Brain size={20} />
                      </div>
                      <div>
                        <div className={styles.statValue}>{embeddingConfig?.current.model || 'N/A'}</div>
                        <div className={styles.statLabel}>Active Model</div>
                      </div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statIcon} style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }}>
                        <Zap size={20} />
                      </div>
                      <div>
                        <div className={styles.statValue}>{embeddingConfig?.current.dimensions || 0}d</div>
                        <div className={styles.statLabel}>Dimensions</div>
                      </div>
                    </div>
                  </div>

                  {/* Active Embedding Config */}
                  {embeddingConfig && (
                    <div className={styles.activeModelSection}>
                      <h3>Active Embedding Configuration</h3>
                      <div className={styles.activeModelCard}>
                        <div className={styles.activeModelInfo}>
                          <div className={styles.activeModelHeader}>
                            <Brain size={20} />
                            <span className={styles.activeModelName}>{embeddingConfig.current.model}</span>
                            <span className={styles.activeModelProvider}>{embeddingConfig.current.provider}</span>
                          </div>
                          <div className={styles.activeModelDetails}>
                            <span>Use Case: <strong>{embeddingConfig.current.useCase}</strong></span>
                            <span>Dimensions: <strong>{embeddingConfig.current.dimensions}</strong></span>
                            <span>Dynamic Provider: <strong>{embeddingConfig.current.usingDynamicProvider ? 'Yes' : 'No'}</strong></span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setActiveTab('models')}>
                          Change Model <ChevronRight size={14} />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Quick Presets */}
                  <div className={styles.presetsSection}>
                    <h3>Quick Presets</h3>
                    <div className={styles.presetGrid}>
                      {presetCards.map((preset) => (
                        <button
                          key={preset.key}
                          className={`${styles.presetCard} ${embeddingConfig?.current.useCase === preset.key ? styles.presetCardActive : ''}`}
                          onClick={() => handleSetUseCase(preset.key)}
                          disabled={isSettingModel}
                        >
                          <div className={styles.presetIcon} style={{ color: preset.color }}>
                            {preset.icon}
                          </div>
                          <div className={styles.presetTitle}>{preset.title}</div>
                          <div className={styles.presetDesc}>{preset.desc}</div>
                          {embeddingConfig?.current.useCase === preset.key && (
                            <CheckCircle size={16} className={styles.presetCheck} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Datasource Quick List */}
                  <div className={styles.dsQuickSection}>
                    <div className={styles.sectionHeader}>
                      <h3>Recent Datasources</h3>
                      <Button variant="ghost" size="sm" onClick={() => setActiveTab('datasources')}>
                        View All <ChevronRight size={14} />
                      </Button>
                    </div>
                    {globalDatasources.length === 0 ? (
                      <div className={styles.emptyState}>
                        <FileText size={32} />
                        <p>No datasources yet. Upload documents to build your knowledge base.</p>
                      </div>
                    ) : (
                      <div className={styles.dsList}>
                        {globalDatasources.slice(0, 5).map((ds) => (
                          <div key={ds.id} className={styles.dsItem}>
                            <FileText size={16} />
                            <span className={styles.dsName}>{ds.name}</span>
                            <span className={styles.dsType}>{ds.type}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ============ QUESTIONS & AUDIT TAB ============ */}
              {location.pathname === '/knowledge' && activeTab === 'questions' && (
                <div className={styles.datasourcesTab}>
                  <div className={styles.sectionHeader}>
                    <h3>Questions & Audit History</h3>
                  </div>
                  <QuestionsAudit datasources={globalDatasources} />
                </div>
              )}

              {/* ============ DATASOURCES TAB ============ */}
              {activeTab === 'datasources' && (
                <div className={styles.datasourcesTab}>
                  <div className={styles.sectionHeader}>
                    <h3>Knowledge Base by Assistant</h3>
                    {assistants.length > 0 && (
                      <div className={styles.assistantSelector}>
                        <Dropdown
                          label=""
                          options={assistants.map(a => ({
                            value: a.id,
                            label: a.name,
                          }))}
                          value={selectedAssistant?.id || ''}
                          onChange={handleAssistantChange}
                          placeholder="Select an assistant"
                        />
                      </div>
                    )}
                  </div>
                  
                  {selectedAssistant ? (
                    <KnowledgeBase
                      assistantId={selectedAssistant.id}
                      assistantName={selectedAssistant.name}
                    />
                  ) : (
                    <div className={styles.emptyState}>
                      <Database size={32} />
                      <p>Select an assistant to manage its knowledge base</p>
                    </div>
                  )}

                  {/* Global Datasources Section */}
                  <div className={styles.globalDsSection}>
                    <div className={styles.sectionHeader}>
                      <h3>Global Datasources</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          setIsLoadingDatasources(true);
                          try {
                            const res = await api.listAllDatasources();
                            if (res.success && res.data) {
                              setGlobalDatasources(Array.isArray(res.data) ? res.data : []);
                            }
                          } catch (err) {
                            console.error(err);
                          } finally {
                            setIsLoadingDatasources(false);
                          }
                        }}
                      >
                        <RefreshCw size={14} /> Refresh
                      </Button>
                    </div>
                    {isLoadingDatasources ? (
                      <div className={styles.loadingState}>Loading datasources...</div>
                    ) : globalDatasources.length === 0 ? (
                      <div className={styles.emptyState}>
                        <FileText size={32} />
                        <p>No global datasources found</p>
                      </div>
                    ) : (
                      <div className={styles.dsGrid}>
                        {globalDatasources.map((ds) => (
                          <div key={ds.id} className={styles.dsCard}>
                            <div className={styles.dsCardHeader}>
                              <FileText size={18} />
                              <h4>{ds.name}</h4>
                            </div>
                            {ds.description && <p className={styles.dsCardDesc}>{ds.description}</p>}
                            <div className={styles.dsCardMeta}>
                              <span className={styles.dsCardType}>{ds.type}</span>
                              <span className={styles.dsCardDate}>
                                {new Date(ds.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <button
                              className={styles.dsDeleteBtn}
                              onClick={async () => {
                                if (window.confirm(`Delete datasource "${ds.name}"?`)) {
                                  try {
                                    await api.deleteGlobalDatasource(ds.id);
                                    setGlobalDatasources(prev => prev.filter(d => d.id !== ds.id));
                                  } catch (err) {
                                    console.error(err);
                                  }
                                }
                              }}
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ============ MODELS TAB ============ */}
              {location.pathname === '/knowledge' && activeTab === 'models' && embeddingConfig && (
                <div className={styles.modelsTab}>
                  {/* Presets */}
                  <div className={styles.presetsSection}>
                    <h3>Use Case Presets</h3>
                    <p className={styles.sectionDesc}>
                      Select a preset to automatically configure the best embedding model for your use case
                    </p>
                    <div className={styles.presetGrid}>
                      {presetCards.map((preset) => (
                        <button
                          key={preset.key}
                          className={`${styles.presetCard} ${embeddingConfig.current.useCase === preset.key ? styles.presetCardActive : ''}`}
                          onClick={() => handleSetUseCase(preset.key)}
                          disabled={isSettingModel}
                        >
                          <div className={styles.presetIcon} style={{ color: preset.color }}>
                            {preset.icon}
                          </div>
                          <div className={styles.presetTitle}>{preset.title}</div>
                          <div className={styles.presetDesc}>{preset.desc}</div>
                          {embeddingConfig.current.useCase === preset.key && (
                            <CheckCircle size={16} className={styles.presetCheck} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* All Models */}
                  <div className={styles.allModelsSection}>
                    <h3>All Available Models</h3>
                    <p className={styles.sectionDesc}>
                      Choose a specific model for fine-grained control over embedding quality and cost
                    </p>
                    <div className={styles.modelsGrid}>
                      {embeddingConfig.models.map((model) => (
                        <ModelCard
                          key={model.model}
                          model={model}
                          isActive={embeddingConfig.current.model === model.model}
                          onSelect={() => handleSelectModel(model)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Provider Availability */}
                  <div className={styles.providerSection}>
                    <h3>Provider Status</h3>
                    <div className={styles.providerGrid}>
                      {Object.entries(embeddingConfig.availableProviders).map(([provider, available]) => (
                        <div key={provider} className={styles.providerCard}>
                          <span className={styles.providerName}>{provider}</span>
                          {available ? (
                            <span className={styles.providerAvailable}>
                              <CheckCircle size={14} /> Available
                            </span>
                          ) : (
                            <span className={styles.providerUnavailable}>
                              <AlertCircle size={14} /> API Key Missing
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ============ SEARCH & TEST TAB ============ */}
              {location.pathname === '/knowledge' && activeTab === 'search' && (
                <div className={styles.searchTab}>
                  {/* Knowledge Search */}
                  <div className={styles.searchSection}>
                    <h3>Knowledge Base Search</h3>
                    <p className={styles.sectionDesc}>
                      Search your knowledge base using semantic similarity to test RAG retrieval
                    </p>
                    {assistants.length > 0 && (
                      <div className={styles.searchAssistantPicker}>
                        <Dropdown
                          label="Assistant"
                          options={assistants.map(a => ({
                            value: a.id,
                            label: a.name,
                          }))}
                          value={selectedAssistant?.id || ''}
                          onChange={handleAssistantChange}
                          placeholder="Select assistant"
                        />
                      </div>
                    )}
                    <div className={styles.searchInputRow}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Enter a search query to test RAG retrieval..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <Button
                        variant="primary"
                        onClick={handleSearch}
                        disabled={isSearching || !searchQuery.trim() || !selectedAssistant}
                      >
                        {isSearching ? 'Searching...' : 'Search'}
                      </Button>
                    </div>
                    {searchResults.length > 0 && (
                      <div className={styles.searchResults}>
                        <h4>Results ({searchResults.length} chunks)</h4>
                        {searchResults.map((result, i) => (
                          <div key={i} className={styles.searchResultItem}>
                            <div className={styles.resultHeader}>
                              <span className={styles.resultScore}>
                                Score: {(result.score || result.similarity || 0).toFixed(3)}
                              </span>
                              {result.source && (
                                <span className={styles.resultSource}>{result.source}</span>
                              )}
                            </div>
                            <p className={styles.resultContent}>
                              {(result.content || result.text || '').substring(0, 300)}...
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Embedding Test */}
                  <div className={styles.testSection}>
                    <h3>Test Embedding</h3>
                    <p className={styles.sectionDesc}>
                      Test the current embedding model by generating a vector for sample text
                    </p>
                    <div className={styles.searchInputRow}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Enter text to generate embedding..."
                        value={testText}
                        onChange={(e) => setTestText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleTestEmbedding()}
                      />
                      <Button
                        variant="primary"
                        onClick={handleTestEmbedding}
                        disabled={isTesting || !testText.trim()}
                      >
                        {isTesting ? 'Testing...' : 'Test'}
                      </Button>
                    </div>
                    {testResult && (
                      <div className={styles.testResultCard}>
                        <div className={styles.testResultRow}>
                          <span>Dimensions:</span>
                          <strong>{testResult.dimensions}</strong>
                        </div>
                        <div className={styles.testResultRow}>
                          <span>Processing Time:</span>
                          <strong>{testResult.processingTime}</strong>
                        </div>
                        {testResult.provider && (
                          <>
                            <div className={styles.testResultRow}>
                              <span>Provider:</span>
                              <strong>{testResult.provider.provider}</strong>
                            </div>
                            <div className={styles.testResultRow}>
                              <span>Model:</span>
                              <strong>{testResult.provider.model}</strong>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};
